<script setup>
defineOptions({ inheritAttrs: false });

import { cn } from '/app/utils/helpers';
import { computed, ref, watch, useSlots, useAttrs } from 'vue';
import { toHTML } from '/app/composables/markdown';
import { truncateHTML } from '/app/utils/helpers';

const props = defineProps({
    tag: {
        type: String,
        default: 'div',
    },
    code: {
        type: [String, Array],
        default: '',
    },
    inline: {
        type: Boolean,
        default: false,
    },
    unfurl: {
        type: Boolean,
        default: false,
    },
    truncate: {
        type: Number,
        default: null,
    },
});

const emit = defineEmits(['link-clicked', 'rendered']);

const slots = useSlots();
const attrs = useAttrs();

const innerHTML = ref('');
const wantsList = ref(false);

const render = (code) => {
    if (!code) {
        const content = slots.default ? slots.default() : [];

        if (content.length > 0) {
            const { children } = content[0];
            code = children;
        }
    }

    wantsList.value = Array.isArray(code);
    if (wantsList.value) {
        code = code.map(c => `- ${c}\n`).join('');
    }

    if (typeof code !== 'string') {
        console.warn(`Invalid type ${typeof code}, expected string`, code);
        code = '';
    }

    const html = toHTML(code, {
        inline: props.inline,
        unfurl: props.unfurl,
        mention: true,
    });

    if (props.truncate) {
        const { content, truncated, count } = truncateHTML(html, { length: props.truncate });

        innerHTML.value = content;
        emit('rendered', { truncated, count });
    } else {
        innerHTML.value = html;
        emit('rendered', { truncated: false, count: 0 });
    }
};

watch(() => [props.code, props.truncate], ([newValue]) => {
    render(newValue);
}, { immediate: true });

const classes = computed(() => cn(
    'prose prose-markdown prose-stone max-w-fit prose-pre:rounded-md prose-p:whitespace-pre-wrap prose-p:break-words leading-6 prose-p:leading-6 prose-pre:bg-[#282c34] prose-p:my-0 prose-blockquote:my-2 prose-blockquote:border-l-4 prose-blockquote:border-dust-400 prose-blockquote:pl-2 prose-blockquote:text-dust-700 prose-blockquote:italic prose-code:before:content-none prose-code:after:content-none prose-code:font-normal [&_:not(pre)>code]:bg-dust-100 [&_:not(pre)>code]:text-dust-700 [&_:not(pre)>code]:px-1.5 [&_:not(pre)>code]:py-0.5 [&_:not(pre)>code]:rounded [&_:not(pre)>code]:text-[0.875em]',
    wantsList.value && 'mb-4',
    attrs.class,
));

// The rendered content is injected via `v-html`, so its anchors are plain DOM
// nodes with no per-link Vue listeners. Delegate from the root: any click that
// lands on (or inside) an `<a>` re-emits as `link-clicked`, letting parents
// attach analytics or intercept navigation without touching the renderer or
// the sanitizer. Passive by default — only consumers that listen do anything.
const onLinkClick = (event) => {
    const anchor = event.target.closest('a');
    if (!anchor) return;

    emit('link-clicked', {
        href: anchor.getAttribute('href'),
        text: anchor.textContent.trim(),
        event,
    });
};
</script>

<template>
    <component :is="tag" :class="classes" v-html="innerHTML" @click="onLinkClick" />
</template>
